import {
  AI_CHAT_TIMELINE_MAX_PAGES,
  AI_CHAT_TIMELINE_PAGE_SIZE,
  aiChatQueryKeys,
} from "@/lib/data/react-query/ai-chat";
import type {
  AiChatStreamEvent,
  AiChatThreadDetail,
  AiChatThreadHeader,
  AiChatThreadPage,
  AiChatTimelineItem,
  AiChatTimelinePage,
} from "@/lib/types/ai-chat";
import type { InfiniteData, QueryClient } from "@tanstack/react-query";
import { layoutFromItem, type ChatLayoutData } from "./chat-layout";

export const uniqueTimelineItems = (
  pages: AiChatTimelinePage[] | undefined,
) => {
  const items = new Map<string, AiChatTimelineItem>();
  for (const page of pages ?? []) {
    for (const item of page.items) {
      const current = items.get(item.id);
      if (!current || item.revision >= current.revision)
        items.set(item.id, item);
    }
  }
  return [...items.values()].sort((left, right) =>
    left.sequence === right.sequence
      ? left.id.localeCompare(right.id)
      : left.sequence - right.sequence,
  );
};

export const updateAiChatThreadHeaderCaches = (
  queryClient: QueryClient,
  header: AiChatThreadHeader,
) => {
  queryClient.setQueryData<AiChatThreadDetail>(
    aiChatQueryKeys.thread(header.id),
    (current) =>
      current &&
      (current.thread.title !== header.title ||
        current.thread.status !== header.status ||
        current.thread.id !== header.id)
        ? { ...current, thread: header }
        : current,
  );
  const listQueries = queryClient
    .getQueryCache()
    .findAll({ queryKey: aiChatQueryKeys.threadLists() });
  for (const query of listQueries) {
    const filter = query.queryKey[2];
    const matchesFilter = filter === "all" || filter === header.status;
    const cached = queryClient.getQueryData<InfiniteData<AiChatThreadPage>>(
      query.queryKey,
    );
    const containsThread = cached?.pages.some((page) =>
      page.threads.some((thread) => thread.id === header.id),
    );
    const existing = cached?.pages
      .flatMap((page) => page.threads)
      .find((thread) => thread.id === header.id);
    if (
      existing &&
      matchesFilter &&
      existing.title === header.title &&
      existing.status === header.status
    )
      continue;
    queryClient.setQueryData<InfiniteData<AiChatThreadPage>>(
      query.queryKey,
      (current) =>
        current
          ? {
              ...current,
              pages: current.pages.map((page) => ({
                ...page,
                threads: page.threads.flatMap((thread) => {
                  if (thread.id !== header.id) return [thread];
                  return matchesFilter ? [header] : [];
                }),
              })),
            }
          : current,
    );
    if (cached && matchesFilter && !containsThread) {
      // Let the server place newly matching threads within its paginated order.
      void queryClient.invalidateQueries({
        queryKey: query.queryKey,
        exact: true,
      });
    }
  }
};

const highestItemRevision = (items: AiChatTimelineItem[], previous = 0) =>
  items.reduce(
    (highest, item) =>
      Number.isFinite(item.revision)
        ? Math.max(highest, item.revision)
        : highest,
    Number.isFinite(previous) ? previous : 0,
  );

const replaceTimeline = (
  queryClient: QueryClient,
  event: Extract<AiChatStreamEvent, { type: "thread.snapshot" }>,
) => {
  const page: AiChatTimelinePage = {
    threadId: event.threadId,
    items: event.items,
    beforeCursor: event.beforeCursor,
    afterCursor: event.afterCursor,
    hasMoreBefore: event.hasMoreBefore,
    hasMoreAfter: event.hasMoreAfter,
    revision: highestItemRevision(
      event.items,
      queryClient.getQueryData<AiChatThreadDetail>(
        aiChatQueryKeys.thread(event.threadId),
      )?.revision,
    ),
  };
  const current = queryClient.getQueryData<InfiniteData<AiChatTimelinePage>>(
    aiChatQueryKeys.timeline(event.threadId),
  );
  if (current?.pages.length) {
    mergeTimelineChanges(queryClient, {
      type: "thread.changes",
      threadId: event.threadId,
      thread: event.thread,
      items: event.items,
      previousCursor: event.cursor,
      nextCursor: event.cursor,
      hasMore: false,
    });
    return;
  }
  queryClient.setQueryData<InfiniteData<AiChatTimelinePage>>(
    aiChatQueryKeys.timeline(event.threadId),
    { pages: [page], pageParams: [{ direction: "initial" }] },
  );
};

const mergeTimelineChanges = (
  queryClient: QueryClient,
  event: Extract<AiChatStreamEvent, { type: "thread.changes" }>,
) => {
  queryClient.setQueryData<InfiniteData<AiChatTimelinePage>>(
    aiChatQueryKeys.timeline(event.threadId),
    (current) => {
      if (!current?.pages.length) return current;
      const changed = new Map(event.items.map((item) => [item.id, item]));
      const present = new Set<string>();
      const pages = current.pages.map((page) => ({
        ...page,
        revision: highestItemRevision(event.items, page.revision),
        items: page.items.map((item) => {
          present.add(item.id);
          const next = changed.get(item.id);
          return next && next.revision >= item.revision ? next : item;
        }),
      }));
      const additions = event.items.filter((item) => !present.has(item.id));
      if (additions.length) {
        const lastPage = pages.at(-1);
        if (lastPage && !lastPage.hasMoreAfter) {
          lastPage.items = [...lastPage.items, ...additions].sort(
            (left, right) => left.sequence - right.sequence,
          );
        }
      }
      // Keep a bounded body window; the complete layout retains every row identity.
      let retained = AI_CHAT_TIMELINE_MAX_PAGES * AI_CHAT_TIMELINE_PAGE_SIZE;
      for (let index = pages.length - 1; index >= 0; index -= 1) {
        const page = pages[index];
        const keep = Math.min(retained, page.items.length);
        if (keep < page.items.length) {
          page.items = page.items.slice(-keep || page.items.length);
          page.hasMoreBefore = true;
        }
        retained -= keep;
      }
      const keptPages = pages.filter((page) => page.items.length);
      const firstKept = pages.findIndex((page) => page.items.length);
      return {
        ...current,
        pages: keptPages.length ? keptPages : pages,
        pageParams:
          firstKept > 0
            ? current.pageParams.slice(firstKept)
            : current.pageParams,
      };
    },
  );
};

export const applyAiChatStreamEvent = (
  queryClient: QueryClient,
  event: AiChatStreamEvent,
) => {
  if (event.type === "thread.snapshot" || event.type === "thread.changes") {
    queryClient.setQueryData<ChatLayoutData>(
      aiChatQueryKeys.layout(event.threadId),
      // The layout query owns revision/membership merging; stream only sends upserts.
      (current) => ({
        items: event.items.map(layoutFromItem),
        revision: highestItemRevision(event.items, current?.revision),
        complete: current?.complete ?? false,
      }),
    );
  }
  if (event.type === "thread.snapshot") {
    replaceTimeline(queryClient, event);
    updateAiChatThreadHeaderCaches(queryClient, event.thread);
    if (event.resyncRequired) {
      void queryClient.invalidateQueries({
        queryKey: aiChatQueryKeys.layout(event.threadId),
        exact: true,
      });
      void queryClient.invalidateQueries({
        queryKey: aiChatQueryKeys.timeline(event.threadId),
        exact: true,
      });
    }
    return;
  }
  if (event.type === "thread.changes") {
    mergeTimelineChanges(queryClient, event);
    updateAiChatThreadHeaderCaches(queryClient, event.thread);
  }
};
